import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft, Pencil, Star } from 'lucide-react'
import { toast } from 'sonner'
import { AttachmentsPanel } from '@/components/crm/attachments-panel'
import { CommentsThread } from '@/components/crm/comments-thread'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { LayoutAwareDetail } from '@/components/crm/layout-aware-detail'
import { TagPicker } from '@/components/crm/tag-picker'
import { Timeline } from '@/components/crm/timeline'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/contacts/$contactId')({
  component: ContactDetailPage,
})

type ContactRecord = {
  id: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  phone?: string | null
  jobTitle?: string | null
  accountId?: string | null
  isPrimaryForAccount?: boolean | null
  ownerUserId?: string | null
  _ext?: Record<string, unknown>
}

type ActivityRow = {
  id: string
  type?: string | null
  subject?: string | null
  dueAt?: string | Date | null
  completedAt?: string | Date | null
}

function formatDate(v: string | Date | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleDateString()
  } catch {
    return '-'
  }
}

function ContactDetailPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { contactId } = Route.useParams()

  const { data, isLoading } = useQuery(
    orpc.crm.contacts.get.queryOptions({ input: { id: contactId } }),
  )

  const { data: activityRows } = useQuery(
    orpc.crm.activities.list.queryOptions({
      input: { limit: 200, offset: 0, relatedToType: 'contact', relatedToId: contactId },
    }),
  )

  const contact = data as ContactRecord | undefined

  const setPrimaryMutation = useMutation(
    orpc.crm.contacts.setPrimary.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.contact.isPrimary'))
        queryClient.invalidateQueries({
          queryKey: orpc.crm.contacts.get.queryKey({ input: { id: contactId } }),
        })
        queryClient.invalidateQueries({ queryKey: orpc.crm.contacts.list.key() })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  if (isLoading || !contact) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const title =
    `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() ||
    contact.email ||
    '(unnamed contact)'

  const activities: ActivityRow[] = (activityRows as ActivityRow[] | undefined) ?? []

  const activityColumns: EntityListColumn<ActivityRow>[] = [
    {
      key: 'type',
      header: t('crm.activity.type'),
      render: (r) => <span className="capitalize">{r.type ?? '-'}</span>,
    },
    { key: 'subject', header: t('crm.activity.subject'), render: (r) => r.subject ?? '-' },
    { key: 'dueAt', header: t('crm.activity.dueAt'), render: (r) => formatDate(r.dueAt) },
    {
      key: 'completedAt',
      header: t('crm.activity.completedAt'),
      render: (r) =>
        r.completedAt ? (
          formatDate(r.completedAt)
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/crm/contacts' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('crm.common.cancel')}
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            {contact.jobTitle && <span>{contact.jobTitle}</span>}
            {contact.email && <span>{contact.email}</span>}
            {contact.ownerUserId && (
              <span>
                {t('crm.common.ownedBy')}: {contact.ownerUserId}
              </span>
            )}
            {contact.isPrimaryForAccount && (
              <Badge variant="outline">{t('crm.contact.isPrimary')}</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={!contact.accountId || contact.isPrimaryForAccount === true}
            onClick={() => setPrimaryMutation.mutate({ id: contactId })}
          >
            <Star className="h-4 w-4 mr-2" />
            {t('crm.contact.setPrimary')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/crm/contacts/$contactId/edit', params: { contactId } })}
          >
            <Pencil className="h-4 w-4 mr-2" />
            {t('crm.common.edit')}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details">{t('navigation.details')}</TabsTrigger>
          <TabsTrigger value="activities">{t('crm.activity.entityPlural')}</TabsTrigger>
          <TabsTrigger value="timeline">{t('navigation.timeline')}</TabsTrigger>
          <TabsTrigger value="tags">{t('navigation.inputTags')}</TabsTrigger>
          <TabsTrigger value="comments">{t('navigation.chat')}</TabsTrigger>
          <TabsTrigger value="attachments">{t('navigation.fileDropzone')}</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <LayoutAwareDetail
            entityKey="contact"
            record={contact as Record<string, unknown> & { id: string }}
          />
        </TabsContent>

        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.activity.entityPlural')}</CardTitle>
            </CardHeader>
            <CardContent>
              <EntityList rows={activities} columns={activityColumns} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.timeline')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline recordType="contact" recordId={contactId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tags">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.inputTags')}</CardTitle>
            </CardHeader>
            <CardContent>
              <TagPicker recordType="contact" recordId={contactId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comments">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.chat')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CommentsThread recordType="contact" recordId={contactId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attachments">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.fileDropzone')}</CardTitle>
            </CardHeader>
            <CardContent>
              <AttachmentsPanel recordType="contact" recordId={contactId} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
